import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Users, ShieldCheck, EnvelopeSimple, Plus, Trash, FloppyDisk,
  CheckCircle, Warning, PaperPlaneTilt, Lock, Eye, Pencil, Check, ArrowsClockwise,
  Stack, ArrowDown, X as XIcon, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const VIEW_TABS = [
  { id: "users",     label: "Users & Invites",      icon: Users },
  { id: "roles",     label: "Roles & Permissions",  icon: ShieldCheck },
  { id: "hierarchy", label: "Approval Hierarchy",   icon: Stack },
  { id: "email",     label: "Email Settings",       icon: EnvelopeSimple },
];

export default function SettingsTab({ currentUser, onPermissionsChanged }) {
  const [view, setView] = useState("users");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/settings/meta").then((r) => setMeta(r.data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  return (
    <div className="space-y-5" data-testid="settings-tab">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-2xl tracking-tight">Settings</h2>
          <div className="text-sm text-slate-400 mt-1">
            Manage who can do what — users, roles, and automated email alerts.
          </div>
        </div>
        <div className="text-[11px] text-slate-500">
          Signed in as <span className="text-cyan-300 font-bold">{currentUser?.email}</span>
          {currentUser?.role && <span className="ml-2 px-2 py-0.5 rounded-sm bg-cyan-900/40 text-cyan-200 border border-cyan-700/60 uppercase tracking-wider text-[9px] font-bold">{currentUser.role}</span>}
        </div>
      </div>

      {/* Sub-tab strip */}
      <div className="inline-flex rounded-md border border-slate-700 bg-slate-900 p-1" data-testid="settings-view-switcher">
        {VIEW_TABS.map((vt) => {
          const Icon = vt.icon;
          const active = view === vt.id;
          return (
            <button
              key={vt.id}
              onClick={() => setView(vt.id)}
              className={
                "inline-flex items-center gap-1.5 px-3 h-8 rounded-sm text-xs font-semibold transition-colors " +
                (active ? "bg-cyan-600 text-white" : "text-slate-300 hover:bg-slate-800")
              }
              data-testid={`settings-view-${vt.id}`}
            >
              <Icon size={14} weight={active ? "duotone" : "regular"} />
              <span>{vt.label}</span>
            </button>
          );
        })}
      </div>

      {!meta ? (
        <div className="text-center py-12 text-slate-500 text-sm">Loading settings…</div>
      ) : (
        <>
          {view === "users" && <UsersPanel meta={meta} currentUser={currentUser} />}
          {view === "roles" && <RolesPanel meta={meta} onPermissionsChanged={onPermissionsChanged} />}
          {view === "hierarchy" && <ApprovalHierarchyPanel meta={meta} />}
          {view === "email" && <EmailPanel meta={meta} />}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Users & Invites Panel
// =============================================================================
function UsersPanel({ meta, currentUser }) {
  const [users, setUsers]     = useState([]);
  const [invites, setInvites] = useState([]);
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole]   = useState("viewer");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, inv, rl] = await Promise.all([
        api.get("/settings/users"),
        api.get("/settings/invites").catch(() => ({ data: [] })),
        api.get("/settings/roles"),
      ]);
      setUsers(u.data || []);
      setInvites(inv.data || []);
      setRoles(rl.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loginUrl = `${window.location.origin}/login`;

  const sendInvite = async () => {
    if (!newEmail || !newEmail.includes("@")) { toast.error("Valid email required"); return; }
    try {
      const r = await api.post("/settings/invites", {
        email: newEmail.trim().toLowerCase(),
        role: newRole,
        login_url: loginUrl,
      });
      const es = r.data?.email_status || {};
      if (es.ok) {
        toast.success(`Invited ${newEmail} — login link emailed`);
      } else if (es.skipped) {
        toast.success(`Invited ${newEmail} (email skipped: ${es.reason})`);
      } else {
        toast.success(`Invited ${newEmail} (email send failed)`);
      }
      setNewEmail("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
  };
  const resendInvite = async (inv) => {
    try {
      await api.post(`/settings/invites/${inv.id}/resend`, { login_url: loginUrl });
      toast.success(`Login link re-sent to ${inv.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
  };
  const cancelInvite = async (inv) => {
    if (!window.confirm(`Cancel invite for ${inv.email}?`)) return;
    try { await api.delete(`/settings/invites/${inv.id}`); toast.success("Invite cancelled"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  const changeRole = async (u, role) => {
    try { await api.patch(`/settings/users/${u.user_id}`, { role }); toast.success(`${u.email} → ${role}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Role change failed"); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Remove user ${u.email}? They will lose access.`)) return;
    try { await api.delete(`/settings/users/${u.user_id}`); toast.success(`Removed ${u.email}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-5">
      {/* Invite form */}
      <div className="border border-slate-700 rounded-md bg-slate-900/60 p-4" data-testid="invite-form">
        <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold mb-2 flex items-center gap-1">
          <Plus size={12} weight="bold" /> Invite a teammate
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Email</Label>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="teammate@yourcompany.com"
              data-testid="invite-email-input"
              className="h-9 bg-slate-950 border-slate-700 text-sm"
            />
          </div>
          <div className="min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Role</Label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="h-9 w-full rounded-sm border border-slate-700 bg-slate-950 text-slate-100 text-sm px-2"
              data-testid="invite-role-select"
            >
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <Button onClick={sendInvite} className="h-9 bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="invite-send-btn">
            <Plus size={13} weight="bold" className="mr-1" /> Invite
          </Button>
        </div>
        <div className="text-[10px] text-slate-500 mt-2">
          The invitee will be granted the chosen role the moment they sign in with Google using this email.
        </div>
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div data-testid="pending-invites">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-2 flex items-center gap-1">
            <Warning size={12} weight="fill" /> Pending invites ({invites.length})
          </div>
          <div className="rounded-md border border-amber-900/50 bg-amber-950/20 divide-y divide-amber-900/40">
            {invites.map((inv) => (
              <div key={inv.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm" data-testid={`invite-row-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-amber-100 font-semibold">{inv.email}</span>
                  <span className="text-amber-300/70 ml-2">→ {inv.role}</span>
                  <span className="text-amber-500/70 ml-2 text-[10px]">by {inv.invited_by}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => resendInvite(inv)}
                    className="text-slate-500 hover:text-cyan-300 p-1"
                    data-testid={`invite-resend-${inv.id}`}
                    title="Re-send the login-link email"
                  >
                    <PaperPlaneTilt size={13} weight="bold" />
                  </button>
                  <button onClick={() => cancelInvite(inv)} className="text-slate-500 hover:text-rose-400 p-1" data-testid={`invite-cancel-${inv.id}`}>
                    <Trash size={13} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing users */}
      <div data-testid="users-list">
        <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold mb-2 flex items-center gap-1">
          <Users size={12} weight="bold" /> Active users ({users.length})
        </div>
        <div className="rounded-md border border-slate-700 bg-slate-900/40 overflow-hidden">
          {loading ? (
            <div className="text-center py-8 text-slate-500 text-sm">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-800 text-[10px] uppercase tracking-wider text-cyan-200">
                <tr><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Role</th><th></th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-slate-800 hover:bg-slate-900/60" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {u.picture
                          ? <img src={u.picture} alt="" className="h-7 w-7 rounded-full" />
                          : <div className="h-7 w-7 rounded-full bg-slate-700 grid place-items-center text-[10px] font-bold text-slate-300">{(u.email||'?').slice(0,2).toUpperCase()}</div>}
                        <div>
                          <div className="text-slate-100 font-semibold">{u.name || u.email.split("@")[0]}</div>
                          <div className="text-[10px] text-slate-400">{u.email}</div>
                        </div>
                        {u.user_id === currentUser?.user_id && <Badge className="bg-cyan-900/40 text-cyan-200 border-cyan-700/60 text-[9px] font-bold uppercase">You</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={u.role || "viewer"}
                        onChange={(e) => changeRole(u, e.target.value)}
                        disabled={u.user_id === currentUser?.user_id && u.role === "owner"}
                        className="h-7 rounded-sm border border-slate-700 bg-slate-950 text-slate-100 text-xs px-2 disabled:opacity-50"
                        data-testid={`user-role-${u.user_id}`}
                      >
                        {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {u.user_id !== currentUser?.user_id && (
                        <button onClick={() => deleteUser(u)} className="text-slate-500 hover:text-rose-400 p-1" data-testid={`user-delete-${u.user_id}`}>
                          <Trash size={13} weight="bold" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Roles & Permissions Panel
// =============================================================================
function RolesPanel({ meta, onPermissionsChanged }) {
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty]     = useState({}); // { roleId: { tabId: { action: bool } } }
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/roles");
      setRoles(r.data || []);
      setDirty({});
    } catch (e) { toast.error("Failed to load roles"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePerm = (roleId, tabId, action) => {
    setDirty((d) => {
      const role = roles.find((r) => r.id === roleId);
      const current = ((d[roleId] || role?.permissions || {})[tabId] || {})[action];
      const next = !current;
      return {
        ...d,
        [roleId]: {
          ...(d[roleId] || role?.permissions || {}),
          [tabId]: { ...((d[roleId] || role?.permissions || {})[tabId] || {}), [action]: next },
        },
      };
    });
  };

  const getPerm = (role, tabId, action) => {
    if (dirty[role.id] && dirty[role.id][tabId] && dirty[role.id][tabId][action] != null) {
      return dirty[role.id][tabId][action];
    }
    return !!(role.permissions || {})[tabId]?.[action];
  };

  const saveRole = async (role) => {
    if (!dirty[role.id]) return;
    setSaving(true);
    try {
      const merged = { ...role.permissions, ...dirty[role.id] };
      await api.put(`/settings/roles/${role.id}`, { permissions: merged });
      toast.success(`${role.name} permissions saved`);
      await load();
      // Tell the shell that permissions changed so the current user's own
      // `/auth/me` (and therefore sidebar visibility + per-tab guards) picks
      // up the update immediately without a hard reload.
      onPermissionsChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500 text-sm">Loading…</div>;

  // Group tabs by category
  const groups = {};
  meta.tabs.forEach((t) => {
    if (!groups[t.group]) groups[t.group] = [];
    groups[t.group].push(t);
  });

  const ACTION_ICONS = { view: Eye, edit: Pencil, delete: Trash, approve: Check };

  return (
    <div className="space-y-5" data-testid="roles-panel">
      <div className="text-xs text-slate-400">
        Toggle <span className="text-cyan-300 font-semibold">View / Edit / Delete / Approve</span> for each role × tab pair. <span className="text-amber-300">Owner</span> is fixed at full access.
      </div>
      {roles.map((role) => {
        const isOwner = role.id === "owner";
        const isDirty = !!dirty[role.id];
        return (
          <div key={role.id} className="border border-slate-700 rounded-md bg-slate-900/40 overflow-hidden" data-testid={`role-card-${role.id}`}>
            <div className="px-4 py-2.5 bg-slate-800/80 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-sm grid place-items-center bg-cyan-900/40 text-cyan-200">
                  {isOwner ? <Lock size={13} weight="bold" /> : <ShieldCheck size={13} weight="duotone" />}
                </div>
                <div>
                  <div className="font-bold text-cyan-100 text-sm">{role.name}</div>
                  <div className="text-[10px] text-slate-400">{role.description}</div>
                </div>
              </div>
              {!isOwner && (
                <Button
                  size="sm"
                  disabled={!isDirty || saving}
                  onClick={() => saveRole(role)}
                  className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40"
                  data-testid={`role-save-${role.id}`}
                >
                  <FloppyDisk size={12} weight="bold" className="mr-1" />
                  {isDirty ? "Save changes" : "Saved"}
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid={`role-matrix-${role.id}`}>
                <thead>
                  <tr className="bg-slate-900 text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
                    <th className="text-left px-3 py-2 min-w-[180px]">Tab</th>
                    {["view", "edit", "delete", "approve"].map((a) => {
                      const Icon = ACTION_ICONS[a];
                      return (
                        <th key={a} className="px-3 py-2 text-center">
                          <div className="inline-flex items-center gap-1 capitalize">
                            <Icon size={11} weight="bold" /> {a}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groups).map(([groupName, tabs]) => (
                    <React.Fragment key={groupName}>
                      <tr><td colSpan={5} className="px-3 py-1.5 bg-slate-950 text-[9px] uppercase tracking-wider font-bold text-slate-500 border-y border-slate-800">{groupName}</td></tr>
                      {tabs.map((t) => (
                        <tr key={t.id} className="border-t border-slate-800 hover:bg-slate-900/50">
                          <td className="px-3 py-1.5 text-slate-200">{t.label}</td>
                          {["view", "edit", "delete", "approve"].map((a) => (
                            <td key={a} className="px-3 py-1.5 text-center">
                              <Checkbox
                                checked={getPerm(role, t.id, a)}
                                disabled={isOwner}
                                onCheckedChange={() => togglePerm(role.id, t.id, a)}
                                data-testid={`perm-${role.id}-${t.id}-${a}`}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Email Settings Panel
// =============================================================================
function EmailPanel({ meta }) {
  const [cfg, setCfg]   = useState(null);
  const [roles, setRoles] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [saving, setSaving]   = useState(false);
  // Per-event "add new email" input state
  const [addInputs, setAddInputs] = useState({});
  // Which event rows are open in the accordion. Start collapsed so the manager
  // sees the full task list at a glance.
  const [expandedEvents, setExpandedEvents] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([api.get("/settings/email"), api.get("/settings/roles")]);
      setCfg(c.data);
      setRoles(r.data || []);
    } catch (e) { toast.error("Failed to load email settings"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const updEvent = (eid, patch) => {
    setCfg((c) => ({ ...c, events: { ...c.events, [eid]: { ...(c.events?.[eid] || {}), ...patch } } }));
  };

  // ---- Recipient (per-email) helpers ----------------------------------------
  const recipientsOf = (eid) => {
    const ec = cfg?.events?.[eid] || {};
    if (Array.isArray(ec.recipients)) return ec.recipients;
    return (ec.emails || []).map((e) => ({ email: e, enabled: true }));
  };
  const setRecipients = (eid, recipients) => {
    const enabledEmails = recipients.filter((r) => r.enabled && r.email).map((r) => r.email);
    updEvent(eid, { recipients, emails: enabledEmails });
  };
  const addRecipient = (eid) => {
    const raw = (addInputs[eid] || "").trim().toLowerCase();
    if (!raw || !raw.includes("@")) { toast.error("Enter a valid email"); return; }
    const cur = recipientsOf(eid);
    if (cur.some((r) => r.email === raw)) {
      toast.error(`${raw} is already on this event`);
      return;
    }
    setRecipients(eid, [...cur, { email: raw, enabled: true }]);
    setAddInputs((s) => ({ ...s, [eid]: "" }));
  };
  const toggleRecipient = (eid, idx) => {
    const cur = recipientsOf(eid).map((r, i) => i === idx ? { ...r, enabled: !r.enabled } : r);
    setRecipients(eid, cur);
  };
  const removeRecipient = (eid, idx) => {
    const cur = recipientsOf(eid).filter((_, i) => i !== idx);
    setRecipients(eid, cur);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/email", cfg);
      toast.success("Email settings saved");
      setCfg(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo.includes("@")) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      await api.post("/settings/email/test", { to: testTo });
      toast.success(`Test email sent to ${testTo}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  if (!cfg) return <div className="text-center py-12 text-slate-500 text-sm">Loading…</div>;

  // Diagnostics — surface why mail might NOT be firing
  const smtpOk      = !!(cfg.smtp_user && cfg.smtp_password);
  const eventsOn    = Object.entries(cfg.events || {}).filter(([, c]) => c.enabled);
  const eventsReady = eventsOn.filter(([, c]) => {
    const r = Array.isArray(c.recipients) ? c.recipients : (c.emails || []).map((e) => ({ email: e, enabled: true }));
    const anyEnabled = r.some((x) => x.enabled && x.email);
    const anyRole = (c.roles || []).length > 0;
    return anyEnabled || anyRole;
  });
  const enabledEventCount = eventsOn.length;
  const readyEventCount   = eventsReady.length;

  return (
    <div className="space-y-5" data-testid="email-panel">
      {/* Diagnostics banner — explains why approval/etc emails may NOT fire */}
      <div
        className={
          "rounded-md border p-3 text-xs flex items-start gap-2 " +
          (!cfg.enabled
            ? "border-amber-700/60 bg-amber-950/30 text-amber-200"
            : !smtpOk
            ? "border-rose-700/60 bg-rose-950/30 text-rose-200"
            : readyEventCount === 0
            ? "border-amber-700/60 bg-amber-950/30 text-amber-200"
            : "border-emerald-800/60 bg-emerald-950/30 text-emerald-200")
        }
        data-testid="email-diagnostics-banner"
      >
        <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
        <div className="leading-relaxed">
          {!cfg.enabled && (
            <>SMTP is <b>disabled globally</b>. Turn on "Enabled" in the SMTP block and click Save to start sending emails for any event.</>
          )}
          {cfg.enabled && !smtpOk && (
            <>SMTP is enabled but the <b>Gmail address</b> / <b>App password</b> are blank. Add them and Save.</>
          )}
          {cfg.enabled && smtpOk && readyEventCount === 0 && (
            <>SMTP works but <b>no event is fully configured</b>. For each event below: tick its checkbox AND add at least one enabled recipient email (or a role).</>
          )}
          {cfg.enabled && smtpOk && readyEventCount > 0 && (
            <>Active: <b>{readyEventCount}</b> of <b>{enabledEventCount || readyEventCount}</b> events ready to fire. Toggle individual emails on/off below without deleting them.</>
          )}
        </div>
      </div>

      {/* SMTP config */}
      <div className="border border-slate-700 rounded-md bg-slate-900/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-cyan-100 flex items-center gap-2">
            <EnvelopeSimple size={15} weight="duotone" /> SMTP Configuration (Gmail)
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <Checkbox checked={!!cfg.enabled} onCheckedChange={(v) => upd({ enabled: !!v })} data-testid="email-enabled-toggle" />
            <span>{cfg.enabled ? "Enabled" : "Disabled"}</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="SMTP Host">
            <Input value={cfg.smtp_host || ""} onChange={(e) => upd({ smtp_host: e.target.value })} className="h-9 bg-slate-950 border-slate-700 text-sm" data-testid="smtp-host" />
          </Field>
          <Field label="SMTP Port">
            <Input type="number" value={cfg.smtp_port || 587} onChange={(e) => upd({ smtp_port: Number(e.target.value) })} className="h-9 bg-slate-950 border-slate-700 text-sm" data-testid="smtp-port" />
          </Field>
          <Field label="Gmail address (smtp user)">
            <Input value={cfg.smtp_user || ""} onChange={(e) => upd({ smtp_user: e.target.value })} placeholder="you@gmail.com" className="h-9 bg-slate-950 border-slate-700 text-sm" data-testid="smtp-user" />
          </Field>
          <Field label="App password (16 chars)">
            <Input type="password" value={cfg.smtp_password || ""} onChange={(e) => upd({ smtp_password: e.target.value })} placeholder="xxxx xxxx xxxx xxxx" className="h-9 bg-slate-950 border-slate-700 text-sm font-mono" data-testid="smtp-password" />
          </Field>
          <Field label="From email (optional)">
            <Input value={cfg.from_email || ""} onChange={(e) => upd({ from_email: e.target.value })} placeholder="leave blank to use SMTP user" className="h-9 bg-slate-950 border-slate-700 text-sm" data-testid="smtp-from-email" />
          </Field>
          <Field label="From name">
            <Input value={cfg.from_name || ""} onChange={(e) => upd({ from_name: e.target.value })} className="h-9 bg-slate-950 border-slate-700 text-sm" data-testid="smtp-from-name" />
          </Field>
        </div>
        <div className="text-[10px] text-slate-500 mt-2">
          For Gmail: generate a 16-character app password at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline">myaccount.google.com/apppasswords</a> (2-Step Verification must be ON).
        </div>
      </div>

      {/* Events */}
      <div className="border border-slate-700 rounded-md bg-slate-900/40 overflow-hidden">
        <div className="px-4 py-3 bg-slate-800/80 border-b border-slate-700 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-sm font-bold text-cyan-100">When are emails sent?</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Click any row to define <b>who</b> receives <b>which</b> notification. Toggle the green
              switch to actually turn the trigger ON — until you do, the event stays muted even with
              recipients configured.
            </div>
          </div>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
            <button
              type="button"
              onClick={() => setExpandedEvents(new Set(meta.events.map((e) => e.id)))}
              className="px-2 h-7 rounded-sm border border-slate-600 text-slate-300 hover:bg-slate-700/60"
              data-testid="email-expand-all"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={() => setExpandedEvents(new Set())}
              className="px-2 h-7 rounded-sm border border-slate-600 text-slate-300 hover:bg-slate-700/60"
              data-testid="email-collapse-all"
            >
              Collapse all
            </button>
          </div>
        </div>
        <div className="divide-y divide-slate-800">
          {meta.events.map((e) => {
            const ec = cfg.events?.[e.id] || { enabled: false, emails: [], roles: [] };
            const isOpen = expandedEvents.has(e.id);
            const recipientsCount = recipientsOf(e.id).filter((r) => r.enabled).length;
            const rolesCount      = (ec.roles || []).length;
            const ready = ec.enabled && (recipientsCount + rolesCount > 0);
            return (
              <div key={e.id} className="px-4 py-3" data-testid={`email-event-${e.id}`}>
                {/* Header row — click to expand, switch to enable */}
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => setExpandedEvents((s) => {
                      const ns = new Set(s);
                      ns.has(e.id) ? ns.delete(e.id) : ns.add(e.id);
                      return ns;
                    })}
                    className="shrink-0 mt-0.5 h-6 w-6 grid place-items-center rounded-sm text-slate-300 hover:text-cyan-200 hover:bg-slate-800/60"
                    aria-label={isOpen ? "Collapse" : "Expand"}
                    data-testid={`email-event-toggle-${e.id}`}
                  >
                    {isOpen ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-sm font-semibold text-slate-100">{e.label}</div>
                      <span
                        className={
                          "inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[9px] font-bold uppercase tracking-wider " +
                          (ready
                            ? "bg-emerald-950/40 border-emerald-700 text-emerald-300"
                            : ec.enabled
                            ? "bg-amber-950/40 border-amber-700 text-amber-300"
                            : "bg-slate-900 border-slate-700 text-slate-500")
                        }
                        data-testid={`email-event-status-${e.id}`}
                        title={
                          ready ? "Will send when this trigger fires"
                            : ec.enabled ? "Enabled but no recipients — add at least one email or role"
                            : "Muted — toggle the switch to enable"
                        }
                      >
                        {ready ? "Active" : ec.enabled ? "Needs recipients" : "Muted"}
                      </span>
                      {recipientsCount > 0 && (
                        <span className="text-[10px] text-cyan-300 font-semibold">
                          {recipientsCount} email{recipientsCount === 1 ? "" : "s"}
                        </span>
                      )}
                      {rolesCount > 0 && (
                        <span className="text-[10px] text-cyan-300 font-semibold">
                          + {rolesCount} role{rolesCount === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    {e.description && (
                      <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                        {e.description}
                      </div>
                    )}
                    {e.trigger && (
                      <div className="text-[10px] text-slate-500 mt-1 font-mono">
                        Trigger: {e.trigger}
                      </div>
                    )}
                  </div>
                  {/* Enable switch on the right */}
                  <button
                    type="button"
                    onClick={() => updEvent(e.id, { enabled: !ec.enabled })}
                    className={
                      "shrink-0 inline-flex items-center h-6 px-2 rounded-full text-[9px] font-bold uppercase tracking-wider transition-colors " +
                      (ec.enabled
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-700 text-slate-300 hover:bg-slate-600")
                    }
                    data-testid={`email-event-enabled-${e.id}`}
                    title={ec.enabled ? "Click to mute this trigger" : "Click to enable"}
                  >
                    {ec.enabled ? "On" : "Off"}
                  </button>
                </div>

                {/* Always-expandable body — recipients editor stays visible whether enabled or not */}
                {isOpen && (
                  <div className="mt-3 ml-9 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1 block">
                        Recipient emails (toggle on/off, no need to delete)
                      </Label>
                      <div className="space-y-1.5" data-testid={`email-event-recipients-${e.id}`}>
                        {recipientsOf(e.id).length === 0 && (
                          <div className="text-[11px] text-slate-500 italic">No recipients yet. Add one below.</div>
                        )}
                        {recipientsOf(e.id).map((r, idx) => (
                          <div
                            key={`${r.email}-${idx}`}
                            className={
                              "flex items-center gap-2 rounded-sm border px-2 py-1.5 transition-colors " +
                              (r.enabled
                                ? "bg-cyan-950/40 border-cyan-800/60 text-cyan-100"
                                : "bg-slate-950 border-slate-800 text-slate-500")
                            }
                            data-testid={`email-recipient-${e.id}-${idx}`}
                          >
                            <button
                              type="button"
                              onClick={() => toggleRecipient(e.id, idx)}
                              className={
                                "shrink-0 inline-flex items-center justify-center h-6 w-12 rounded-sm text-[9px] font-bold uppercase tracking-wider transition-colors " +
                                (r.enabled
                                  ? "bg-emerald-600 text-white hover:bg-emerald-500"
                                  : "bg-slate-700 text-slate-200 hover:bg-slate-600")
                              }
                              data-testid={`email-recipient-toggle-${e.id}-${idx}`}
                              title={r.enabled ? "Click to deactivate (without removing)" : "Click to activate"}
                            >
                              {r.enabled ? "On" : "Off"}
                            </button>
                            <span
                              className={"flex-1 text-xs font-mono truncate " + (r.enabled ? "" : "line-through opacity-60")}
                              title={r.email}
                            >
                              {r.email}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeRecipient(e.id, idx)}
                              className="h-6 w-6 grid place-items-center rounded-sm text-slate-400 hover:bg-rose-900/40 hover:text-rose-300"
                              data-testid={`email-recipient-remove-${e.id}-${idx}`}
                              title="Remove this email entirely"
                            >
                              <Trash size={12} weight="bold" />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center gap-1.5 pt-1">
                          <Input
                            value={addInputs[e.id] || ""}
                            onChange={(ev) => setAddInputs((s) => ({ ...s, [e.id]: ev.target.value }))}
                            onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); addRecipient(e.id); } }}
                            placeholder="add email and press Enter"
                            className="h-8 flex-1 bg-slate-950 border-slate-700 text-xs"
                            data-testid={`email-recipient-input-${e.id}`}
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => addRecipient(e.id)}
                            className="h-8 bg-cyan-700 hover:bg-cyan-600 text-white px-2.5"
                            data-testid={`email-recipient-add-${e.id}`}
                          >
                            <Plus size={12} weight="bold" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <Field label="Also send to anyone with role">
                      <div className="flex flex-wrap gap-2 pt-1">
                        {roles.map((r) => {
                          const on = (ec.roles || []).includes(r.id);
                          return (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => {
                                const arr = new Set(ec.roles || []);
                                on ? arr.delete(r.id) : arr.add(r.id);
                                updEvent(e.id, { roles: Array.from(arr) });
                              }}
                              data-testid={`email-event-role-${e.id}-${r.id}`}
                              className={
                                "px-2 py-1 rounded-sm text-[10px] uppercase tracking-wider font-bold border transition-colors " +
                                (on
                                  ? "bg-cyan-600 border-cyan-500 text-white"
                                  : "bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800")
                              }
                            >
                              {r.name}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Save + Test */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button onClick={save} disabled={saving} className="h-9 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="email-save-btn">
          <FloppyDisk size={13} weight="bold" className="mr-1" /> {saving ? "Saving…" : "Save settings"}
        </Button>
        <div className="flex items-center gap-2">
          <Input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="recipient@yourcompany.com"
            className="h-9 w-[260px] bg-slate-950 border-slate-700 text-sm"
            data-testid="email-test-to"
          />
          <Button onClick={sendTest} disabled={sending} className="h-9 bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="email-test-btn">
            <PaperPlaneTilt size={13} weight="bold" className="mr-1" /> {sending ? "Sending…" : "Send test"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1 block">{label}</Label>
      {children}
    </div>
  );
}


// =============================================================================
// Approval Hierarchy Panel
// =============================================================================
function ApprovalHierarchyPanel({ meta }) {
  const docTypes = meta?.approval_doc_types || [];
  const [activeDoc, setActiveDoc] = useState(docTypes[0]?.id || "new_po");
  const [roles, setRoles] = useState([]);
  const [hierarchies, setHierarchies] = useState({}); // { doc_type: {enabled, levels, updated_at, updated_by} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftEnabled, setDraftEnabled] = useState(false);
  const [draftLevels, setDraftLevels] = useState([]); // [{role_id}]
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rl, hh] = await Promise.all([
        api.get("/settings/roles"),
        api.get("/settings/approval-hierarchy"),
      ]);
      setRoles(rl.data || []);
      const byType = {};
      (hh.data || []).forEach((h) => { byType[h.doc_type] = h; });
      setHierarchies(byType);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load approval hierarchies");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // When user switches doc-type or fresh hierarchies arrive, hydrate the draft
  useEffect(() => {
    const cur = hierarchies[activeDoc];
    if (cur) {
      setDraftEnabled(!!cur.enabled);
      setDraftLevels((cur.levels || []).map((l) => ({
        role_id: l.role_id,
        approver_emails_str: (l.approver_emails || []).join(", "),
      })));
    } else {
      setDraftEnabled(false);
      setDraftLevels([]);
    }
    setDirty(false);
  }, [activeDoc, hierarchies]);

  const addLevel = () => {
    setDraftLevels((prev) => [...prev, { role_id: roles[0]?.id || "viewer", approver_emails_str: "" }]);
    setDirty(true);
  };
  const removeLevel = (idx) => {
    setDraftLevels((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const setLevelRole = (idx, role_id) => {
    setDraftLevels((prev) => prev.map((l, i) => i === idx ? { ...l, role_id } : l));
    setDirty(true);
  };
  const setLevelEmails = (idx, value) => {
    setDraftLevels((prev) => prev.map((l, i) => i === idx ? { ...l, approver_emails_str: value } : l));
    setDirty(true);
  };
  const moveLevel = (idx, dir) => {
    setDraftLevels((prev) => {
      const next = [...prev];
      const tgt = idx + dir;
      if (tgt < 0 || tgt >= next.length) return prev;
      [next[idx], next[tgt]] = [next[tgt], next[idx]];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    // Validation: every level must have a role
    if (draftEnabled && draftLevels.length === 0) {
      toast.error("Add at least one level, or disable the hierarchy");
      return;
    }
    if (draftLevels.some((l) => !l.role_id)) {
      toast.error("Every level needs a role");
      return;
    }
    setSaving(true);
    try {
      const body = {
        enabled: draftEnabled,
        levels: draftLevels.map((l, i) => ({
          level: i + 1,
          role_id: l.role_id,
          approver_emails: (l.approver_emails_str || "")
            .split(/[,\n]/)
            .map((e) => e.trim().toLowerCase())
            .filter((e) => e && e.includes("@")),
        })),
      };
      const r = await api.put(`/settings/approval-hierarchy/${activeDoc}`, body);
      setHierarchies((prev) => ({ ...prev, [activeDoc]: r.data }));
      setDirty(false);
      toast.success(`Hierarchy saved for ${docTypes.find((d) => d.id === activeDoc)?.label || activeDoc}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const cur = hierarchies[activeDoc];

  return (
    <div className="space-y-5" data-testid="approval-hierarchy-panel">
      <div className="rounded-md border border-slate-700 bg-gradient-to-br from-slate-900 to-slate-950 p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Stack size={18} weight="duotone" className="text-cyan-300" />
              <h3 className="font-heading font-bold text-base text-cyan-50">Approval Hierarchy</h3>
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-xl">
              Build a sequential approval chain per document type. Each Approve click advances the
              chain to the next role — final approval happens at the top level. Any rejection
              along the way kills the chain.
            </p>
          </div>
          {cur?.updated_at && (
            <div className="text-[10px] text-slate-500">
              Last updated <span className="text-slate-300">{new Date(cur.updated_at).toLocaleString()}</span>
              {cur.updated_by && <span> · by <span className="text-slate-300">{cur.updated_by}</span></span>}
            </div>
          )}
        </div>

        {/* Document type selector */}
        <div className="mt-4 inline-flex rounded-md border border-slate-700 bg-slate-900 p-1" data-testid="hierarchy-doctype-switcher">
          {docTypes.map((d) => {
            const active = activeDoc === d.id;
            return (
              <button
                key={d.id}
                onClick={() => setActiveDoc(d.id)}
                className={
                  "px-3 h-8 rounded-sm text-xs font-semibold transition-colors " +
                  (active ? "bg-cyan-600 text-white" : "text-slate-300 hover:bg-slate-800")
                }
                data-testid={`hierarchy-doctype-${d.id}`}
              >
                {d.label}
                {hierarchies[d.id]?.enabled && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm bg-emerald-900/60 text-emerald-300 border border-emerald-700/60 text-[9px] font-bold uppercase tracking-wider">
                    On
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500 text-sm">Loading hierarchy…</div>
      ) : (
        <div className="rounded-md border border-slate-700 bg-slate-900/60 p-5 space-y-5" data-testid="hierarchy-editor">
          {/* Enable toggle */}
          <div className="flex items-center gap-3">
            <Checkbox
              checked={draftEnabled}
              onCheckedChange={(v) => { setDraftEnabled(!!v); setDirty(true); }}
              data-testid="hierarchy-enable-checkbox"
            />
            <div>
              <div className="text-sm font-semibold text-slate-100">Enable multi-level approval for this document</div>
              <div className="text-xs text-slate-400">
                When disabled, approvals work as before (single click = approved).
              </div>
            </div>
          </div>

          {/* Levels editor */}
          <div className="space-y-2" data-testid="hierarchy-levels-list">
            {draftLevels.length === 0 && (
              <div className="rounded-sm border border-dashed border-slate-700 bg-slate-950/40 p-6 text-center text-xs text-slate-500" data-testid="hierarchy-no-levels">
                No levels yet. Add Level 1 below.
              </div>
            )}
            {draftLevels.map((lv, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col gap-3 rounded-md border border-slate-700 bg-slate-950/60 p-3" data-testid={`hierarchy-level-${i + 1}`}>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-cyan-700 text-cyan-50 text-xs font-bold ring-2 ring-cyan-500/40">
                      L{i + 1}
                    </span>
                    <div className="flex-1">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1 block">
                        Approves with role
                      </Label>
                      <select
                        value={lv.role_id || ""}
                        onChange={(e) => setLevelRole(i, e.target.value)}
                        className="w-full h-9 bg-slate-900 border border-slate-700 text-sm text-slate-100 rounded-sm px-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        data-testid={`hierarchy-level-${i + 1}-role`}
                      >
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveLevel(i, -1)}
                        disabled={i === 0}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-cyan-300"
                        title="Move up"
                        data-testid={`hierarchy-level-${i + 1}-up`}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveLevel(i, 1)}
                        disabled={i === draftLevels.length - 1}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-cyan-300"
                        title="Move down"
                        data-testid={`hierarchy-level-${i + 1}-down`}
                      >
                        ↓
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLevel(i)}
                        className="h-8 w-8 p-0 text-rose-400 hover:bg-rose-950/40 hover:text-rose-300"
                        title="Remove this level"
                        data-testid={`hierarchy-level-${i + 1}-remove`}
                      >
                        <Trash size={13} weight="bold" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1 block">
                      Approver email(s) — optional
                    </Label>
                    <Input
                      value={lv.approver_emails_str || ""}
                      onChange={(e) => setLevelEmails(i, e.target.value)}
                      placeholder="ashish@mrchows.co.in, krish@mrchows.co.in"
                      className="w-full h-9 bg-slate-900 border-slate-700 text-sm text-slate-100 rounded-sm px-2 focus:ring-2 focus:ring-cyan-500 placeholder:text-slate-600"
                      data-testid={`hierarchy-level-${i + 1}-emails`}
                    />
                    <div className="text-[10px] text-slate-500 mt-1">
                      Leave blank to allow any user with the {roles.find(r => r.id === lv.role_id)?.name || lv.role_id} role. Comma-separate to pin this level to specific people (useful when the same role stands at multiple levels).
                    </div>
                  </div>
                </div>
                {i < draftLevels.length - 1 && (
                  <div className="flex justify-center text-slate-600">
                    <ArrowDown size={14} weight="bold" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Add level + Save */}
          <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={addLevel}
              className="h-9 rounded-sm border-cyan-700 text-cyan-200 hover:bg-cyan-900/30 hover:text-cyan-100 bg-slate-900"
              data-testid="hierarchy-add-level-btn"
            >
              <Plus size={13} weight="bold" className="mr-1" /> Add level
            </Button>
            <div className="flex items-center gap-2">
              {dirty && <span className="text-[11px] text-amber-400 font-semibold">Unsaved changes</span>}
              <Button
                size="sm"
                onClick={save}
                disabled={saving || !dirty}
                className="h-9 bg-cyan-600 hover:bg-cyan-500 text-white disabled:opacity-50"
                data-testid="hierarchy-save-btn"
              >
                <FloppyDisk size={13} weight="bold" className="mr-1" />
                {saving ? "Saving…" : "Save hierarchy"}
              </Button>
            </div>
          </div>

          {/* Preview chain */}
          {draftEnabled && draftLevels.length > 0 && (
            <div className="mt-4 rounded-md border border-cyan-900/40 bg-cyan-950/20 p-3" data-testid="hierarchy-preview">
              <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold mb-2">Approval chain preview</div>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                {draftLevels.map((lv, i) => {
                  const r = roles.find((rr) => rr.id === lv.role_id);
                  const emails = (lv.approver_emails_str || "")
                    .split(/[,\n]/).map((e) => e.trim()).filter(Boolean);
                  return (
                    <React.Fragment key={i}>
                      <span className="inline-flex flex-col items-start gap-0.5 px-2.5 py-1 rounded-md bg-slate-900 border border-cyan-800 text-cyan-100 font-semibold">
                        <span className="flex items-center gap-1.5">
                          <span className="text-[9px] text-cyan-300 font-bold">L{i + 1}</span>
                          {r?.name || lv.role_id}
                        </span>
                        {emails.length > 0 && (
                          <span className="text-[10px] text-cyan-400/80 font-normal">
                            {emails.join(", ")}
                          </span>
                        )}
                      </span>
                      {i < draftLevels.length - 1 && <span className="text-cyan-500">→</span>}
                    </React.Fragment>
                  );
                })}
                <span className="text-cyan-500">→</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-900/50 border border-emerald-700 text-emerald-200 font-semibold">
                  <CheckCircle size={12} weight="bold" /> Approved
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
